<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  definition: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const NODE_ICONS = {
  start: 'i-lucide-play',
  message: 'i-lucide-message-square-text',
  wait_input: 'i-lucide-git-branch',
  branch: 'i-lucide-split',
  add_label: 'i-lucide-tag',
  assign_team: 'i-lucide-users',
  whatsapp_template: 'i-lucide-message-circle',
  end: 'i-lucide-flag',
};

const NODE_ACCENT = {
  start: 'border-l-n-teal-9 bg-n-teal-2/30',
  message: 'border-l-n-blue-9 bg-n-blue-2/30',
  wait_input: 'border-l-n-amber-9 bg-n-amber-2/30',
  branch: 'border-l-n-slate-9 bg-n-slate-3/40',
  add_label: 'border-l-n-violet-9 bg-n-violet-2/30',
  assign_team: 'border-l-n-slate-10 bg-n-slate-3/50',
  whatsapp_template: 'border-l-n-green-9 bg-n-green-2/30',
  end: 'border-l-n-ruby-9 bg-n-ruby-2/30',
};

function orderNodeIds(def) {
  if (!def?.nodes || !def.start_node_id) return { ordered: [], orphans: [] };
  const nodes = def.nodes;
  const start = String(def.start_node_id);
  const queue = [start];
  const ordered = [];
  const seen = new Set();

  while (queue.length) {
    const id = queue.shift();
    if (!seen.has(id) && nodes[id]) {
      seen.add(id);
      ordered.push(id);
      const n = nodes[id];
      if (n.next) queue.push(String(n.next));
      if (n.default_next) queue.push(String(n.default_next));
      if (Array.isArray(n.branches)) {
        n.branches.forEach(b => {
          if (b?.next) queue.push(String(b.next));
        });
      }
    }
  }

  const orphans = Object.keys(nodes).filter(id => !seen.has(id));
  return { ordered, orphans };
}

const timeline = computed(() => {
  if (!props.definition) return { ordered: [], orphans: [] };
  return orderNodeIds(props.definition);
});

function nodeLabel(type) {
  const labels = {
    start: t('FLOWS.NODE_TYPE_START'),
    message: t('FLOWS.NODE_TYPE_MESSAGE'),
    wait_input: t('FLOWS.NODE_TYPE_WAIT_INPUT'),
    branch: t('FLOWS.NODE_TYPE_BRANCH'),
    add_label: t('FLOWS.NODE_TYPE_ADD_LABEL'),
    assign_team: t('FLOWS.NODE_TYPE_ASSIGN_TEAM'),
    whatsapp_template: t('FLOWS.NODE_TYPE_WHATSAPP_TEMPLATE'),
    end: t('FLOWS.NODE_TYPE_END'),
  };
  return labels[type] || type;
}

function truncate(text, max = 120) {
  if (!text || typeof text !== 'string') return '';
  const s = text.trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
}
</script>

<template>
  <div class="flex flex-col gap-0">
    <template v-if="!definition || !definition.nodes">
      <p class="text-sm text-n-slate-11">
        {{ $t('FLOWS.TIMELINE_EMPTY') }}
      </p>
    </template>
    <template v-else>
      <div
        v-for="(id, index) in timeline.ordered"
        :key="id"
        class="relative flex gap-3"
      >
        <div
          class="flex w-9 flex-col items-center flex-shrink-0"
          :aria-hidden="true"
        >
          <div
            class="flex h-9 w-9 items-center justify-center rounded-full border border-n-weak bg-n-solid-2 text-n-slate-11"
          >
            <Icon
              :icon="
                NODE_ICONS[definition.nodes[id]?.type] || 'i-lucide-circle-dot'
              "
              class="size-4"
            />
          </div>
          <div
            v-if="
              index < timeline.ordered.length - 1 || timeline.orphans.length
            "
            class="w-px flex-1 min-h-[1rem] bg-gradient-to-b from-n-weak to-transparent"
          />
        </div>
        <div
          class="mb-6 min-w-0 flex-1 rounded-xl border border-n-weak border-l-4 bg-n-solid-2 pl-4 pr-4 py-3 shadow-sm"
          :class="
            NODE_ACCENT[definition.nodes[id]?.type] || 'border-l-n-slate-8'
          "
        >
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-mono text-xs text-n-slate-10">{{ id }}</span>
            <span
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium capitalize text-n-slate-12"
            >
              {{ nodeLabel(definition.nodes[id]?.type) }}
            </span>
          </div>

          <p
            v-if="
              definition.nodes[id]?.type === 'message' &&
              definition.nodes[id]?.content
            "
            class="mt-2 text-sm leading-relaxed text-n-slate-12"
          >
            {{ truncate(definition.nodes[id].content) }}
          </p>

          <p
            v-if="definition.nodes[id]?.type === 'whatsapp_template'"
            class="mt-2 text-sm text-n-slate-12"
          >
            {{
              $t('FLOWS.NODE_TEMPLATE_WITH_NAME', {
                name: definition.nodes[id].template_name,
              })
            }}
          </p>

          <p
            v-if="definition.nodes[id]?.type === 'add_label'"
            class="mt-2 text-sm text-n-slate-12"
          >
            {{
              $t('FLOWS.NODE_LABEL_WITH_VALUE', {
                value: definition.nodes[id].label,
              })
            }}
          </p>

          <p
            v-if="definition.nodes[id]?.type === 'assign_team'"
            class="mt-2 text-sm text-n-slate-12"
          >
            {{
              $t('FLOWS.NODE_TEAM_WITH_ID', {
                id: definition.nodes[id].team_id,
              })
            }}
          </p>

          <ul
            v-if="
              definition.nodes[id]?.type === 'wait_input' &&
              definition.nodes[id]?.branches?.length
            "
            class="mt-3 space-y-2 border-t border-n-weak pt-3"
          >
            <li
              v-for="(br, bi) in definition.nodes[id].branches"
              :key="bi"
              class="text-sm text-n-slate-12"
            >
              <Icon
                icon="i-lucide-arrow-right"
                class="mr-1 inline size-3.5 align-text-bottom text-n-slate-10"
              />
              {{
                br.conditions?.length
                  ? $t('FLOWS.BRANCH_TO_ADVANCED', { next: br.next })
                  : $t('FLOWS.BRANCH_TO', {
                      match: br.match_type,
                      value: br.value,
                      next: br.next,
                    })
              }}
            </li>
            <li
              v-if="definition.nodes[id].default_next"
              class="text-sm text-n-slate-11"
            >
              <Icon
                icon="i-lucide-git-merge"
                class="mr-1 inline size-3.5 align-text-bottom"
              />
              {{
                $t('FLOWS.DEFAULT_TO', {
                  node: definition.nodes[id].default_next,
                })
              }}
            </li>
          </ul>

          <p
            v-if="
              definition.nodes[id]?.next &&
              definition.nodes[id]?.type !== 'wait_input'
            "
            class="mt-2 flex flex-wrap items-center gap-1 text-xs text-n-slate-11"
          >
            <Icon icon="i-lucide-arrow-down" class="size-3.5" />
            {{ $t('FLOWS.NODE_NEXT') }}
            <span class="font-mono text-n-slate-12">{{
              definition.nodes[id].next
            }}</span>
          </p>
        </div>
      </div>

      <div
        v-if="timeline.orphans.length"
        class="mt-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 px-4 py-3"
      >
        <p class="text-xs font-medium text-n-slate-11">
          {{ $t('FLOWS.ORPHAN_NODES') }}
        </p>
        <ul class="mt-2 flex flex-wrap gap-2">
          <li
            v-for="oid in timeline.orphans"
            :key="oid"
            class="rounded-md bg-n-alpha-2 px-2 py-1 font-mono text-xs text-n-slate-12"
          >
            {{ oid }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
